import { motion } from "framer-motion";

// Approximate coordinates within a 1000x500 SVG viewBox
const HUBS = [
  { id: "sofia", name: "Sofia · HQ", x: 545, y: 165, primary: true },
  { id: "london", name: "London", x: 478, y: 138 },
  { id: "berlin", name: "Berlin", x: 520, y: 138 },
  { id: "newyork", name: "New York", x: 290, y: 175 },
  { id: "saopaulo", name: "São Paulo", x: 340, y: 320 },
  { id: "cairo", name: "Cairo", x: 575, y: 200 },
  { id: "dubai", name: "Dubai", x: 625, y: 215 },
  { id: "mumbai", name: "Mumbai", x: 680, y: 230 },
  { id: "singapore", name: "Singapore", x: 770, y: 280 },
  { id: "tokyo", name: "Tokyo", x: 840, y: 175 },
  { id: "sydney", name: "Sydney", x: 855, y: 365 },
  { id: "capetown", name: "Cape Town", x: 550, y: 360 },
  { id: "toronto", name: "Toronto", x: 270, y: 155 },
];

const ROUTES = [
  ["sofia", "london"],
  ["sofia", "berlin"],
  ["sofia", "newyork"],
  ["sofia", "dubai"],
  ["sofia", "mumbai"],
  ["sofia", "tokyo"],
  ["sofia", "saopaulo"],
  ["sofia", "cairo"],
  ["sofia", "singapore"],
  ["sofia", "capetown"],
  ["sofia", "sydney"],
  ["sofia", "toronto"],
];

function curvedPath(a, b) {
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const offset = dist * 0.22;
  // Perpendicular curl
  const nx = -dy / dist;
  const ny = dx / dist;
  const cx = midX + nx * offset;
  const cy = midY + ny * offset - 12;
  return `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
}

export default function GlobalMap() {
  const hubMap = Object.fromEntries(HUBS.map((h) => [h.id, h]));

  return (
    <section
      id="global"
      data-testid="global-map-section"
      className="relative py-24 md:py-32 bg-[var(--sci-bg)] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 10 / Global Research Community
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
              Connecting researchers <em className="not-italic text-[var(--sci-teal)]">across borders</em>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-[var(--sci-text-muted)] leading-relaxed text-base md:text-lg">
            From Sofia to Singapore — our editorial network spans continents,
            convening clinical and surgical expertise from major academic
            institutions worldwide.
          </p>
        </div>

        <div className="relative bg-white border border-[var(--sci-border)] p-4 md:p-10 overflow-hidden">
          {/* World map silhouette via Pexels */}
          <div className="relative w-full aspect-[2/1]">
            <img
              src="https://images.pexels.com/photos/5725589/pexels-photo-5725589.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="World map"
              className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-multiply"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-transparent to-white/40 pointer-events-none" />

            {/* SVG overlay */}
            <svg
              viewBox="0 0 1000 500"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="xMidYMid slice"
              role="img"
              aria-label="Animated publication routes from Sofia to global hubs"
            >
              {/* Animated routes */}
              {ROUTES.map(([from, to], i) => {
                const a = hubMap[from];
                const b = hubMap[to];
                if (!a || !b) return null;
                const d = curvedPath(a, b);
                return (
                  <g key={`${from}-${to}`}>
                    <motion.path
                      d={d}
                      fill="none"
                      stroke="rgba(11,22,44,0.18)"
                      strokeWidth="1"
                      initial={{ pathLength: 0, opacity: 0 }}
                      whileInView={{ pathLength: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 1.6,
                        delay: 0.1 * i,
                        ease: "easeInOut",
                      }}
                    />
                    <motion.path
                      d={d}
                      fill="none"
                      stroke="rgba(0,122,138,0.85)"
                      strokeWidth="1.4"
                      strokeDasharray="3 8"
                      className="dash-flow"
                      initial={{ pathLength: 0, opacity: 0 }}
                      whileInView={{ pathLength: 1, opacity: 0.9 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 1.8,
                        delay: 0.1 * i + 0.2,
                        ease: "easeInOut",
                      }}
                    />
                  </g>
                );
              })}

              {/* Hubs */}
              {HUBS.map((h, i) => (
                <g key={h.id}>
                  <motion.circle
                    cx={h.x}
                    cy={h.y}
                    r={h.primary ? 7 : 4}
                    fill={h.primary ? "#007A8A" : "#0B162C"}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.05 * i }}
                  />
                  {h.primary && (
                    <motion.circle
                      cx={h.x}
                      cy={h.y}
                      r={14}
                      fill="none"
                      stroke="#007A8A"
                      strokeWidth="1.5"
                      initial={{ scale: 0.5, opacity: 0.8 }}
                      animate={{ scale: 2.4, opacity: 0 }}
                      transition={{
                        duration: 2.2,
                        repeat: Infinity,
                        ease: "easeOut",
                      }}
                    />
                  )}
                  <text
                    x={h.x + 12}
                    y={h.y + 4}
                    className="fill-[var(--sci-navy)]"
                    style={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontSize: h.primary ? 12 : 10,
                      fontWeight: h.primary ? 600 : 400,
                      letterSpacing: "0.05em",
                    }}
                  >
                    {h.name}
                  </text>
                </g>
              ))}
            </svg>
          </div>

          {/* Floating stat nodes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-8 md:mt-12">
            {[
              { v: "40+", l: "Countries" },
              { v: "180+", l: "Editorial members" },
              { v: "12", l: "Specialty areas" },
              { v: "100%", l: "Open access" },
            ].map((s, i) => (
              <div
                key={s.l}
                data-testid={`global-stat-${i}`}
                className="border border-[var(--sci-border)] p-5 md:p-6"
              >
                <div className="font-serif-display text-3xl md:text-4xl text-[var(--sci-navy)] leading-none">
                  {s.v}
                </div>
                <div className="overline text-[var(--sci-text-muted)] mt-3">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
